import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { PartnerSearchType } from '@evimo/types/enums/partner-search-type';
import { useDebouncedValue } from '~/app/hooks/useDebouncedValue';
import { PartnersService } from '~/app/services/partners/partners.service';
import type { PartnerOrgSelection } from '@/app/landing/sla/shared/types';

export type PartnerOrgAutocompleteOption = {
  value: string;
  name: string;
  alreadyLinked: boolean;
};

function normalizePartnerLabel(value: string): string {
  return value.trim().toLowerCase();
}

export function usePartnerOrgAutocomplete(
  organizationId: string,
  searchType: PartnerSearchType,
) {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue, 1000);

  const isLandlord = searchType === PartnerSearchType.LANDLORD;

  const landlordQuery = useQuery({
    queryKey: ['partners', 'landlords', organizationId] as const,
    queryFn: () => PartnersService.listLandlords(organizationId),
    enabled: isLandlord,
  });

  const oemQuery = useQuery({
    queryKey: ['partners', 'oems', organizationId] as const,
    queryFn: () => PartnersService.listOems(organizationId),
    enabled: !isLandlord,
  });

  const linkedItems = useMemo(() => {
    if (isLandlord) {
      return (landlordQuery.data?.items ?? []).map((item) => ({
        partnerOrgId: item.partnerOrgId,
        partnerName: item.partnerName,
      }));
    }
    return (oemQuery.data?.items ?? []).map((item) => ({
      partnerOrgId: item.partnerOrgId,
      partnerName: item.partnerName,
    }));
  }, [isLandlord, landlordQuery.data, oemQuery.data]);

  const linkedPartnerOrgIds = useMemo(
    () => new Set(linkedItems.map((item) => item.partnerOrgId)),
    [linkedItems],
  );

  const linkedPartnerNames = useMemo(
    () =>
      new Set(
        linkedItems.map((item) => normalizePartnerLabel(item.partnerName)),
      ),
    [linkedItems],
  );

  const isPartnerAlreadyLinked = useCallback(
    (partner: Pick<PartnerOrgSelection, 'id' | 'name'> | null): boolean => {
      if (!partner) {
        return false;
      }

      if (partner.id && linkedPartnerOrgIds.has(partner.id)) {
        return true;
      }

      return linkedPartnerNames.has(normalizePartnerLabel(partner.name));
    },
    [linkedPartnerNames, linkedPartnerOrgIds],
  );

  const searchQuery = useQuery({
    queryKey: [
      'partners',
      'search',
      organizationId,
      searchType,
      debouncedSearch,
    ],
    queryFn: () =>
      PartnersService.searchPartners(
        organizationId,
        debouncedSearch,
        searchType,
      ),
    enabled: debouncedSearch.trim().length > 0,
    staleTime: 30_000,
  });

  const options: PartnerOrgAutocompleteOption[] = useMemo(
    () =>
      (searchQuery.data ?? []).map((result) => ({
        value: result.id,
        name: result.name,
        alreadyLinked: linkedPartnerOrgIds.has(result.id),
      })),
    [linkedPartnerOrgIds, searchQuery.data],
  );

  const isSearching =
    (searchValue.trim().length > 0 &&
      searchValue.trim() !== debouncedSearch.trim()) ||
    searchQuery.isFetching;

  const enrichPartner = useCallback(
    (partner: PartnerOrgSelection | null): PartnerOrgSelection | null => {
      if (!partner) {
        return null;
      }

      return {
        ...partner,
        alreadyLinked: isPartnerAlreadyLinked(partner),
      };
    },
    [isPartnerAlreadyLinked],
  );

  return {
    enrichPartner,
    isPartnerAlreadyLinked,
    isSearching,
    options,
    searchValue,
    setSearchValue,
  };
}
